<script>
import lazyLoadComponent from '@router/lazyload-component'
import { mapState, mapGetters } from 'vuex'
import BaseButtonDialog from '@components/BaseButtonDialog'
import { debounce, cloneDeep } from 'lodash'
import productMixin from '@/src/mixins/product'
import helperMixin from '@/src/mixins/helperMixin'
import {
  genderFilter,
  orderStatusFilter,
  bookingStatusMap,
  bookingStatusFilter,
  orderPaymentMethodMap,
  orderPaymentMethodFilter,
  orderSourceMap,
  orderSourceFilter,
  paymentStatusMapFilter,
} from '@utils/filter-value-to-text'
import formatDate, { formatRequestData } from '@utils/format-date'
import pageMixin from '@src/mixins/pagination'
import moment from 'moment'
export default {
  page: {
    title: '訂單查詢',
    meta: [{ name: 'description', content: 'Order List' }],
  },
  filters: {
    genderFilter,
    orderStatusFilter,
    bookingStatusFilter,
    orderPaymentMethodFilter,
    orderSourceFilter,
    formatDate,
    paymentStatusMapFilter,
  },
  components: {
    BaseButtonDialog,
    BaseStateLabel: () => lazyLoadComponent(import('@components/BaseStateLabel')),
    ReservationOrderDetail: () => lazyLoadComponent(import('@components/Orders/ReservationOrderDetail')),
    // PurchaseProducts: () => lazyLoadComponent(import('@components/Orders/PurchaseProducts')),
    ReservationOrderForm: () => lazyLoadComponent(import('@components/Orders/ReservationOrderForm')),
  },
  mixins: [pageMixin, productMixin, helperMixin],
  props: {
    fetchTypes: {
      type: Array,
      default: () => {
        return []
      },
    },
  },
  data() {
    return {
      componentKey: 0,
      drawerVisible: false,
      drawerOrderData: '',
      getRowKeys(row) {
        return row.id
      },
      getRowKeysForContainer(row) {
        return row.displayId
      },
      expandRowKeys: [],
      filterData: {
        status: '',
        search: '',
        source: '',
        dateType: 'create', // create 訂單成立日期、 use 訂單使用日期
        startDate: '',
        endDate: '',
        serviceStartDate: '',
        serviceEndDate: '',
        paymentMethod: 'All',
        productId: '',
        order_by: 'created_at',
        order: 'desc',
      },
      statusList: { ...bookingStatusMap },
      sourceList: Object.freeze(orderSourceMap),
      paymentMethodList: Object.freeze(this.reduceMethod({ ...orderPaymentMethodMap })),
      pickerOptions: {
        shortcuts: [
          {
            text: '最近一週',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
              picker.$emit('pick', [start, end])
            },
          },
          {
            text: '最近一個月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
              picker.$emit('pick', [start, end])
            },
          },
          {
            text: '最近三個月',
            onClick(picker) {
              const end = new Date()
              const start = new Date()
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90)
              picker.$emit('pick', [start, end])
            },
          },
        ],
      },
      loadedOrders: [], // 原始訂單
      loaderOrdersGroupByContainerId: {}, // 原始訂單（經由containerDisplayId group過的）

      queryProductName: '',
      tryingToGetOrder: false,
      tryingToExport: false,
      submitForm: [],
      submitRules: {
        title: [{ required: true, message: 'empty', trigger: 'change' }],
      },
      productList: [],
      isFilterChange: false,
      appStyle: process.env.VUE_APP_BUILD_MODE === 'admin-app-build',
      limitLength: 0,

      containerInfo: [], // 搜尋回來的container資料（內部會有小訂單
      pageLimit: 15, // 每頁的資料限制大小
      updateKey: 0,
    }
  },
  inject: ['productId'],
  computed: {
    ...mapState('modifyOrder', ['modifyMemberInfo', 'modifyOrder']),
    ...mapGetters('modifyOrder', ['isModified']),
    filterDateRange: {
      get() {
        return [this.filterData.startDate, this.filterData.endDate]
      },
      set(value) {
        const [start, end] = value || ['', '']
        this.filterData.startDate = start
        this.filterData.endDate = end
      },
    },
    filterServiceDateRange: {
      get() {
        return [this.filterData.serviceStartDate, this.filterData.serviceEndDate]
      },
      set(value) {
        const [start, end] = value || ['', '']
        this.filterData.serviceStartDate = start
        this.filterData.serviceEndDate = end
      },
    },
    fetchDataParams() {
      const routeQuery = this.$route.query
      const queryData = Object.assign(routeQuery, this.pageQuery)
      const { startDate, endDate, dateType, serviceStartDate, serviceEndDate, ...filterDataWithoutDate } = this.filterData
      const searchWithDate = startDate && endDate
      const searchWithServiceDate = serviceStartDate && serviceEndDate

      let searchParams = filterDataWithoutDate

      if (searchWithDate) {
        searchParams.startDate = formatRequestData(startDate)
        searchParams.endDate = formatRequestData(endDate)
      }
      if (searchWithServiceDate) {
        searchParams.serviceStartDate = formatRequestData(serviceStartDate)
        searchParams.serviceEndDate = formatRequestData(serviceEndDate)
      }
      if (searchWithDate || searchWithServiceDate) searchParams = { ...searchParams, dateType }

      return { ...searchParams, ...queryData, types: this.fetchTypes, limit: 15 }
    },
    /**
     * @description container的search
     */
    fetchDataParamsForContainer() {
      return {
        is_temp: 0, // 必定為非卡單的狀態
        startDate: this.filterData.startDate ? moment(this.filterData.startDate).format('YYYY-MM-DD') : '',
        endDate: this.filterData.endDate ? moment(this.filterData.endDate).format('YYYY-MM-DD') : '',
        serviceStartDate: this.filterData.serviceStartDate ? moment(this.filterData.serviceStartDate).format('YYYY-MM-DD') : '',
        serviceEndDate: this.filterData.serviceEndDate ? moment(this.filterData.serviceEndDate).format('YYYY-MM-DD') : '',
        status: this.filterData.status,
        paymentMethod: this.filterData.paymentMethod === 'All' ? '' : this.filterData.paymentMethod,
        productId: this.filterData.productId,
        search: this.filterData.search,
        limit: 15,
        page: this.currentPage,
        types: ['booking'],
      }
    },
    checkOrderType() {
      return this.drawerOrderData.params && this.drawerOrderData.params[0].type === 'goods' ? 'goods' : 'reservation'
    },
  },
  watch: {
    currentPage: {
      handler: 'tryToFetchList',
    },
    filterData: {
      handler() {
        this.isFilterChange = true
      },
      deep: true,
    },
    drawerVisible(value) {
      if (!value) {
        if (this.$route.query.type === 'order') {
          this.$router.push({
            name: 'orders',
            params: {
              activeTab: '0',
            },
            query: {},
          })
        }
        this.$store.commit('modifyOrder/CLEAR_DATA')
      } else {
        this.updateKey++
      }
    },
  },
  beforeCreate() {
    if (this.productId && this.productId.length > 0) {
      this.filterData.productId = this.productId
    }
  },
  created() {
    this.tryToSearchProduct()
    if (this.$route.query.type === 'order') {
      this.tryToGetOrder(this.$route.query.id)
    }
    delete this.statusList['1']
  },
  mounted() {
    // 如果一開始有productId 則走這邊
    if (this.productId && this.productId.length > 0) {
      this.filterData.productId = this.productId
    }
    this.tryToFetchList()
  },
  methods: {
    formatTimeMethod(time) {
      return moment(time).format('YYYY/MM/DD HH:mm')
    },
    /**
     * @description 專門處理免費part
     */
    filterPaymentMethodInVue(text) {
      if (text === 'FREE') {
        return 'Free'
      } else {
        return text
      }
    },
    /**
     * @description 結帳成功並且關閉
     */
    async forTabProductSaveSuccessAndClose() {
      await this.closeTabProductDetail()
    },
    async closeTabProductDetail() {
      try {
        await this.tryToFetchList()
        this.drawerVisible = false
      } catch (error) {
        console.error(error.message)
      }
    },
    /**
     * @description 獲取此訂單內容-api
     */
    async onlyFetchSingleOrder(orderId, cb) {
      try {
        const newDrawerOrderData = await this.$store.dispatch('order/fetchSingle', orderId)
        this.drawerOrderData = this.initSpaceHelpers(Object.assign({}, newDrawerOrderData))
        await cb()
      } catch (error) {
        console.error(error.message)
      }
    },
    reduceMethod(list) {
      if (!this.$hasDeposit()) {
        delete list.Deposit
      }
      if (!this.$cashFlowSettingPass()) {
        delete list.Vacc
        delete list.Credit
      }
      return { ...list }
    },
    async handleExport() {
      if (this.tryingToExport) return
      this.tryingToExport = true
      const { fileName, csvData } = await this.$store.dispatch('order/simpleExport', this.fetchDataParams)
      const csvFormatData = csvData.map((row) => Object.values(row).join(',')).join('\n')

      const blob = new Blob(['\uFEFF', csvFormatData], {
        type: 'text/csv;charset=utf-8;',
      })

      const url = window.URL.createObjectURL(blob)

      const tempLink = document.createElement('a')
      tempLink.download = fileName
      tempLink.href = url
      tempLink.click()
      window.URL.revokeObjectURL(url)
      this.tryingToExport = false
    },
    tableRowClassName({ row }) {
      const rowStatus = row.status
      const usedStatus = row.groupSize / row.usedSize
      if (rowStatus === 0) return 'order--canceled'
      if (usedStatus === 1) return 'order--used'
      if (row.id === this.expandRowKeys[0]) return 'expandedRow'
    },
    tableRowClassNameForContainer({ row }) {
      const rowStatus = row.status
      const usedStatus = row.groupSize / row.usedSize
      if (rowStatus === 0) return 'order--canceled'
      if (usedStatus === 1) return 'order--used'
      if (row.displayId === this.expandRowKeys[0]) return 'expandedRow'
    },
    /**
     * @description 取得order資料
     */
    async tryToGetOrder(orderId) {
      try {
        this.tryingToGetOrder = true
        this.drawerOrderData = await this.$store.dispatch('order/fetchSingle', orderId)
        this.drawerVisible = true
        this.$refs.orderDetail.checkResultAndPayStatus = false
        this.$refs.orderDetail.completeReserveAndPayStatus = false
      } catch {
        // this.drawerVisible = false
      } finally {
        this.tryingToGetOrder = false
      }
    },
    /**
     * @description 有搜尋條件的fetchList
     */
    async tryToFetchListFilter() {
      await this.tryToFetchListForContainer()
      // 當篩選前不在第一頁的時候，跳回第一頁
      if (this.currentPage !== 1) {
        this.currentPage = 1
      }
      // fetchList 似乎沒有作用了，先註解掉
      // const { items, productName, total } = await this.$store.dispatch('order/fetchList', this.fetchDataParams)
      // this.loadedOrders = items
      // this.groupByObjectKey()
      // this.queryProductName = productName
      // this.$nextTick(() => {
      //   this.$refs.pagination && this.$refs.pagination.updateTotal(total)
      // })
      // this.isFilterChange = false
    },
    /**
     * @description 當前頁面限制
     */
    currentPageLimit() {
      try {
        const remainder = this.$refs.pagination.total % this.$refs.pagination.pageSize
        const qotient = this.$refs.pagination.total / this.$refs.pagination.pageSize
        const totalPage = remainder ? parseInt(qotient) + 1 : parseInt(qotient)
        if (this.currentPage > totalPage) {
          this.currentPage = totalPage
        }
      } catch (error) {
        console.error(error.message)
      }
    },
    /**
     * @description 一開始進入畫面的fetchList
     */
    async tryToFetchList() {
      try {
        await this.tryToFetchListForContainer()

        // fetchList 似乎沒有作用了，先註解掉
        // const { items, productName, total } = await this.$store.dispatch('order/fetchList', this.fetchDataParams)
        // this.loadedOrders = items
        // this.groupByObjectKey()
        // this.queryProductName = productName
        // this.$nextTick(() => {
        //   this.$refs.pagination && this.$refs.pagination.updateTotal(total)
        //   this.currentPageLimit()
        // })
      } catch (error) {
        console.error(error.message)
      }
    },
    /**
     * @description 一開始進入畫面的fetchList
     */
    async tryToFetchListForContainer() {
      try {
        const { items, pagination } = await this.$store.dispatch('order/orderContainerSearch', this.fetchDataParamsForContainer)
        this.containerInfo = cloneDeep(items) // 不要顯示被取消的container
        this.composeContainerInfoPet()
        this.$nextTick(() => {
          this.pageLimit = pagination.per_page
          this.$refs.pagination && this.$refs.pagination.updateTotal(pagination.total)
          this.currentPageLimit()
        })
      } catch (error) {
        console.error(error.message)
      }
    },
    /**
     * @description 組成使用人資訊/寵物資訊
     */
    composeContainerInfoPet() {
      this.containerInfo.forEach((infoItem) => {
        const { orders } = infoItem
        if (orders.length > 0) {
          let fullNameItem = orders.reduce((accu, cur) => {
            accu.push(cur.fullName)
            return accu
          }, [])
          let uniqueFullNameArr = Array.from(new Set(fullNameItem)).join(', ')
          this.$set(infoItem, 'uniqueFullName', uniqueFullNameArr)
        }
      })
    },
    /**
     * @description 利用containerDisplayId 做顯示
     */
    groupByObjectKey() {
      try {
        this.loaderOrdersGroupByContainerId = this.loadedOrders.reduce((accu, cur) => {
          const { orderContainerDisplayId, childrenOrders } = cur
          this.$set(cur, 'id', `${cur.id} (主)`)
          // push 主訂單
          if (accu[orderContainerDisplayId]) {
            accu[orderContainerDisplayId].push(cur)
          } else {
            accu[orderContainerDisplayId] = [cur]
          }
          // push 子訂單
          if (childrenOrders.length > 0) {
            childrenOrders.forEach((item) => {
              accu[orderContainerDisplayId].push(item)
            })
          }
          return accu
        }, {})
      } catch (error) {
        console.error(error.message)
      }
    },
    clearSearchQuery() {
      this.$router.push(this.$route.path)
    },
    async tryToSearchProduct() {
      const { items } = await this.$store.dispatch('product/fetchList', { limit: 10000, page: 1, paginate: 0 })
      this.productList = items
      this.productList = this.filterProductInMixin(this.productList)
    },
    /**
     * @description 點擊[訂單管理]，的任一列做的動作
     * @param {Object} row：該列資訊
     * @param {Object} column：該欄資訊
     */
    onCellClick(row, column) {
      if (column.type === 'expand') return // 若該欄已經是展開按鈕，則return
      this.tryToGetOrder(row.orders[0].id)
    },
    excludeCanceledPrice(order) {
      let total = order.allTotal
      if (order.addOrders.length) {
        total = order.addOrders
          .map((subOrder) => subOrder.items)
          .flat()
          .reduce((acc, item) => (item.status === 0 ? acc + item.price : acc), total)
      }
      return total
    },
    handleExpand(row) {
      if (this.expandRowKeys[0] === row.id) {
        this.expandRowKeys = []
      } else {
        this.expandRowKeys = [row.id]
      }
    },
    /**
     * @description 展開該列
     */
    handleExpandForContainer(row) {
      if (this.expandRowKeys[0] === row.displayId) {
        this.expandRowKeys = []
      } else {
        this.expandRowKeys = [row.displayId]
      }
    },
    sortChange({ column, prop, order }) {
      if (order) {
        this.filterData.order_by = prop
        this.filterData.order = order === 'ascending' ? 'asc' : 'desc'
        this.currentPage = 1
        this.tryToFetchList()
      }
    },
    /**
     * @description 給定row的資訊，返回這個Container的所有訂單
     */
    returnOrderInContainer(row) {
      try {
        const { orders } = row
        for (let i = 0, len = orders.length; i < len; i++) {
          const { childrenOrders } = orders[i]
          for (let j = 0, len = childrenOrders.length; j < len; j++) {
            if (childrenOrders[j].hasOwnProperty('orderItems') && childrenOrders[j].orderItems.length > 0) {
              let payTotal = childrenOrders[j].orderItems.reduce((accu, cur) => {
                accu = accu + cur.total
                return accu
              }, 0)
              this.$set(childrenOrders[j], 'payTotal', payTotal)
            }
          }
          if (orders[i].hasOwnProperty('orderItems') && orders[i].orderItems.length > 0) {
            let payTotal = orders[i].orderItems.reduce((accu, cur) => {
              accu = accu + cur.total
              return accu
            }, 0)
            this.$set(orders[i], 'payTotal', payTotal)
          }
        }
        return row.orders
      } catch (error) {
        console.error(error.message)
      }
    },
    getPaymentMethod(arr) {
      let result = 'undefined'
      if (Array.isArray(arr) && arr.length === 1) {
        result = arr[0]
      }
      return result
    },
    getInstallmentUnpaidCount(orderArr) {
      let result = 0
      if (Array.isArray(orderArr)) {
        let UnpaidOrderArr = orderArr.filter((order) => {
          return order.paymentStatus !== 'PAID'
        })
        result = UnpaidOrderArr.length
      }
      return result
    },
    /**
     * @description 取得寵物的所有名稱
     */
    collectionPetName(row) {
      try {
        const { orderContainerDisplayId } = row
        let petNameList = []
        if (orderContainerDisplayId in this.loaderOrdersGroupByContainerId) {
          this.loaderOrdersGroupByContainerId[orderContainerDisplayId].forEach((item) => {
            petNameList.push(item.pet.name)
          })
          const set = new Set(petNameList)
          const uniqueArr = Array.from(set)
          return uniqueArr.join(', ')
        } else {
          this.limitLength = 0
        }
      } catch (error) {
        console.error(error.message)
      }
    },
    tryToSearchByInput: debounce(function() {
      this.tryToFetchList()
    }, 300),
  },
}
</script>

<template>
  <div>
    <div :class="$style.btnTop" class="btn-top-space-between">
      <div class="side search">
        <el-input v-model.trim.lazy="filterData.search" clearable placeholder="輸入使用人姓名、手機號碼或訂單編號" @input="tryToSearchByInput">
          <BaseIcon slot="suffix" name="search" class="el-input__icon" />
        </el-input>
      </div>
      <div class="side btn ml-md-3">
        <el-button class="is-round" plain @click="handleExport"> <BaseIcon name="file-export" /> 訂單匯出 </el-button>
        <el-button class="is-round" type="primary" @click="$router.push({ name: 'schedule-check-in' })"> <BaseIcon name="plus" /> 代客預約服務 </el-button>
      </div>
    </div>
    <BaseTableSearch
      v-slot="{ fields }"
      class="isForTab"
      hide-action
      :default-value.sync="filterData"
      :class="$style.filter"
      @handleExport="handleExport"
      @reset="tryToFetchList"
      @tryToFetchList="tryToFetchListFilter"
    >
      <!-- <el-form-item prop="status" style="width: 16%">
        <el-select v-model="fields.status" placeholder="選擇訂單狀態" @change="tryToFetchListFilter">
          <el-option label="所有狀態" value></el-option>
          <el-option v-for="(status, statusValue) in statusList" :key="statusValue" :label="status.text"
            :value="statusValue"></el-option>
        </el-select>
      </el-form-item> -->
      <el-form-item prop="paymentMethod" style="width: 16%">
        <el-select v-model="fields.paymentMethod" placeholder="選擇付款方式" @change="tryToFetchListFilter">
          <el-option label="所有付款方式" value="All"></el-option>
          <el-option v-for="(label, value) in paymentMethodList" :key="value" :label="label" :value="value"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item prop="productId" style="width: 16%">
        <el-select v-model="fields.productId" placeholder="選擇分店" @change="tryToFetchListFilter">
          <el-option label="所有分店" value></el-option>
          <el-option v-for="(product, index) in productList" :key="`product-${index}`" :label="product.name" :value="product.id"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item style="width: 34%">
        <el-date-picker
          v-model="filterDateRange"
          type="daterange"
          align="right"
          unlink-panels
          range-separator="至"
          start-placeholder="訂單起始日期"
          end-placeholder="訂單結束日期"
          :picker-options="pickerOptions"
          @change="tryToFetchListFilter"
        ></el-date-picker>
      </el-form-item>
      <el-form-item style="width: 34%">
        <el-date-picker
          v-model="filterServiceDateRange"
          type="daterange"
          align="right"
          unlink-panels
          range-separator="至"
          start-placeholder="服務起始日期"
          end-placeholder="服務結束日期"
          :picker-options="pickerOptions"
          @change="tryToFetchListFilter"
        ></el-date-picker>
      </el-form-item>
    </BaseTableSearch>
    <div class="isStylePageContent">
      <div class="el-table-wrapper">
        <!-- container search -->
        <el-table
          v-if="true"
          v-loading.fullscreen.lock="tryingToGetOrder"
          element-loading-text="取得訂單資訊中"
          :data="containerInfo"
          :default-sort="{ prop: 'created', order: 'descending' }"
          :row-key="getRowKeysForContainer"
          :row-class-name="tableRowClassNameForContainer"
          :expand-row-keys="expandRowKeys"
          @cell-click="onCellClick"
          @expand-change="handleExpandForContainer"
          @sort-change="sortChange"
        >
          <el-table-column v-slot="{ row }" type="expand">
            <div v-for="(item, index) in returnOrderInContainer(row)" :key="index" :class="$style['order-expanded2']">
              <div class="expandedItems">
                <!-- 預約狀態 -->
                <div>
                  <BaseStateLabel state="secondary" text>{{ item.status | bookingStatusFilter('text') }}</BaseStateLabel>
                </div>
                <!-- 使用人名 -->
                <div>
                  <p>{{ item.fullName || '' }}</p>
                </div>
                <!-- 訂單編號 -->
                <div>
                  <p style="display: none;">#{{ item.id }}</p>
                  <p>預約時間：{{ formatTimeMethod(item.startDate) }}</p>
                </div>
                <!-- 付款狀態 -->
                <div>
                  <template v-if="item.status === 0">
                    <BaseStateLabel state="secondary-light" text>{{ item.status | bookingStatusFilter('text') }} </BaseStateLabel>
                  </template>
                  <template v-else>
                    <BaseStateLabel :state="item.paymentStatus | paymentStatusMapFilter('color')" text>{{ item.paymentStatus | paymentStatusMapFilter('text') }}</BaseStateLabel>
                    <br />
                    <BaseStateLabel class="fs-2sm" state="secondary-light" text>{{ filterPaymentMethodInVue(item.paymentMethod) | orderPaymentMethodFilter }}</BaseStateLabel>
                  </template>
                </div>
                <!-- 此訂單細節(美容服務or加購) -->
                <div>
                  <div v-for="(paramsItem, itemIndex) in item.orderItems" :key="itemIndex" class="order-item">
                    <div>
                      <div style="color: #41695b">
                        <span v-if="paramsItem.type === 'coupon'">優惠券</span>
                        <span v-if="paramsItem.type === '+'">加價</span>
                        <span v-if="paramsItem.type === '-'">減價</span>
                        <span v-if="paramsItem.type === '%'">折扣</span>
                        {{ paramsItem.title }} x{{ paramsItem.qty }}
                      </div>
                    </div>
                    <div>
                      <div class="Jost">${{ paramsItem.total }}</div>
                    </div>
                  </div>
                  <div class="order-item">
                    <div>小計</div>
                    <div class="Jost">${{ item.payTotal }}</div>
                  </div>
                </div>
              </div>
              <el-divider v-if="item['childrenOrders'].length > 0" class="dividerToRight"></el-divider>
              <el-divider v-else></el-divider>
              <!-- 子訂單迴圈 -->
              <div v-for="(childrenItems, childrenIndex) in item['childrenOrders']" :key="childrenIndex" style="position: relative;">
                <div :class="$style.next"> </div>
                <div class="childrenExpandedItems">
                  <!-- 預約狀態 -->
                  <div>
                    <BaseStateLabel state="secondary" text>{{ childrenItems.status | bookingStatusFilter('text') }} </BaseStateLabel>
                  </div>
                  <!-- 使用人名 -->
                  <div>
                    <p>{{ childrenItems.fullName || '' }}</p>
                  </div>
                  <!-- 訂單編號 -->
                  <div>
                    <p style="display: none;">#{{ childrenItems.id }}</p>
                    <p>預約時間：{{ formatTimeMethod(childrenItems.startDate) }}</p>
                  </div>
                  <!-- 付款狀態 -->
                  <div>
                    <template v-if="childrenItems.status === 0">
                      <BaseStateLabel state="secondary-light" text>{{ childrenItems.status | bookingStatusFilter('text') }} </BaseStateLabel>
                    </template>
                    <template v-else>
                      <BaseStateLabel :state="childrenItems.paymentStatus | paymentStatusMapFilter('color')" text>{{ childrenItems.paymentStatus | paymentStatusMapFilter('text') }}</BaseStateLabel>
                      <br />
                      <BaseStateLabel class="fs-2sm" state="secondary-light" text>{{ filterPaymentMethodInVue(childrenItems.paymentMethod) | orderPaymentMethodFilter }}</BaseStateLabel>
                    </template>
                  </div>
                  <!-- 此訂單細節(美容服務or加購) -->
                  <div>
                    <div v-for="(paramsItem, itemIndex) in childrenItems.orderItems" :key="itemIndex" class="order-item">
                      <div>
                        <div style="color: #41695b">
                          <span v-if="paramsItem.type === 'coupon'">優惠券</span>
                          <span v-if="paramsItem.type === '+'">加價</span>
                          <span v-if="paramsItem.type === '-'">減價</span>
                          <span v-if="paramsItem.type === '%'">折扣</span>
                          {{ paramsItem.title }} x{{ paramsItem.qty }}
                        </div>
                      </div>
                      <div>
                        <div class="Jost">${{ paramsItem.total }}</div>
                      </div>
                    </div>
                    <div class="order-item">
                      <div>小計</div>
                      <div class="Jost">${{ childrenItems.payTotal }}</div>
                    </div>
                  </div>
                </div>
                <el-divider v-if="item['childrenOrders'].length - 1 === childrenIndex"></el-divider>
                <el-divider v-if="item['childrenOrders'].length - 1 !== childrenIndex" class="dividerToRight"></el-divider>
              </div>
              <!-- 應付總額 -->
              <div v-if="row.orders.length - 1 === index" class="footer">
                <div class="total Jost"
                  ><span class="text-secondary tRight">應付總額</span> <span class="tLeft">${{ row.allPayTotal }}</span>
                </div>
                <div v-if="row.allRefundTotal > 0" class="total Jost alert-word"
                  ><span class="text-secondary tRight">退款</span>
                  <span class="tLeft">- {{ row.allRefundTotal }}</span>
                </div>
                <div class="total Jost"
                  ><span class="text-secondary tRight">實付總額</span> <span class="tLeft">${{ row.allActualPayTotal }}</span>
                </div>
                <!-- 如果沒有未付款訂單 -->
                <div class="unpaid Jost"
                  ><span class="text-secondary tRight">未付款金額</span>
                  <span class="tLeft">${{ row.allUnpaidTotal }}</span>
                </div>
              </div>
            </div>
          </el-table-column>
          <el-table-column v-slot="{ row }" label="訂單編號" prop="created_at" min-width="150" sortable>
            <div class="Jost fs-sm fw-medium">{{ `#${row.displayId}` }}</div>
            <div class="fs-2sm text-secondary-light">{{ row.created_at | formatDate('dateTime') }}</div>
          </el-table-column>
          <el-table-column v-slot="{ row }" :label="`${$t('orderManage.USER_AND_PET')}`" width="180">
            <div>{{ row.fullName }} {{ row.gender | genderFilter }}</div>
            <div class="fs-2sm text-secondary-light">{{ row.uniqueFullName }}</div>
            <div class="fs-2sm  text-secondary-light">{{ `${row.phoneCode} ${row.phoneNumber}` }}</div>
          </el-table-column>
          <el-table-column v-slot="{ row }" label="付款方式" prop="paymentMethod" min-width="150" sortable>
            <template>
              <!-- 付款狀態 -->
              <BaseStateLabel :state="row.paymentStatus | paymentStatusMapFilter('color')" text>
                {{ row.paymentStatus | paymentStatusMapFilter('text') }}
                <span v-if="getPaymentMethod(row.paymentMethod) === 'Installment' && getInstallmentUnpaidCount(row.installments) > 0" style="color: #F25555"
                  >({{ getInstallmentUnpaidCount(row.installments) }}期未付款)</span
                >
                <span v-if="row.paymentStatus === 'PAID' && row.haveUnpaidOrder" style="color: #F25555">(部分付款)</span>
                <span v-if="row.cancel_at !== null && row.cancel_at.length > 0" style="color: #2D385F">(已取消)</span>
              </BaseStateLabel>
              <br />
              <!-- 付款方式 -->
              <template v-for="(methodItem, methodIndex) in row.paymentMethod">
                <template v-if="methodItem">
                  <span v-if="methodIndex > 0" :key="`span${methodIndex}`">、</span>
                  <BaseStateLabel :key="methodIndex" class="fs-2sm" state="secondary-light" text>{{
                    filterPaymentMethodInVue(methodItem) | orderPaymentMethodFilter(row.installments.length)
                  }}</BaseStateLabel>
                </template>
              </template>
              <br />
              <!-- 發票 -->
              <span v-if="row.invoiceNumber">{{ row.invoiceNumber }}</span>
            </template>
          </el-table-column>
          <el-table-column v-slot="{ row }" label="分店與付款時間" prop="date" min-width="140">
            <div class="fs-2sm text-secondary-light">{{ row.paid_at }} </div>
            <div class="fs-2sm text-secondary-light">{{ row.products.join('、') }}</div>
          </el-table-column>
          <el-table-column v-slot="{ row }" label="總金額" min-width="80">
            <div class="Jost text-secondary-light">${{ row.allPayTotal }}</div>
          </el-table-column>
          <el-table-column min-width="40">
            <el-tooltip content="查看訂單詳情" placement="top">
              <el-button type="text">
                <BaseIcon name="ellipsis-v" />
              </el-button>
            </el-tooltip>
          </el-table-column>
        </el-table>
      </div>
      <BasePagination ref="pagination" :page-size="pageLimit" :current-page.sync="currentPage" />
    </div>

    <BaseButtonDialog only-dialog floating />
    <el-drawer
      ref="orderFormEdit"
      class="only-close"
      :class="{ appStyle: appStyle }"
      :visible.sync="drawerVisible"
      append-to-body
      :size="$root.isSmallDevice ? '100%' : '90%'"
      destroy-on-close
      :wrapper-closable="false"
      custom-class="order-drawer"
    >
      <ReservationOrderDetail
        v-if="!isModified"
        ref="orderDetail"
        :key="updateKey"
        :order-type="checkOrderType"
        :default-value.sync="drawerOrderData"
        @update="tryToFetchList"
        @onlyFetchSingleOrder="onlyFetchSingleOrder"
        @closeTabProductDetail="closeTabProductDetail"
        @forTabProductSaveSuccessAndClose="forTabProductSaveSuccessAndClose"
      />
      <ReservationOrderForm v-else :order-type="'reservation'" :schedule-detail="modifyOrder.scheduleItem" @store="closeTabProductDetail" />
    </el-drawer>
  </div>
</template>
<style lang="scss" module>
:global {
  :local(.filter) {
    & > div {
      // flex-wrap: wrap;
      .el-form-item {
        @include max-lg {
          width: 100% !important;
        }
      }

      & + button {
        align-self: baseline;
      }
    }
  }
}

.order-expanded {
  :global {
    .item {
      display: flex;
      align-items: center;
      padding: 20px;
      position: relative;
      align-items: flex-start;

      &.header {
        padding: 15px 20px;
      }

      &.header,
      &.no-divider {
        &:after {
          display: none;
        }
      }

      > div {
        flex: 1 1 auto;
        padding: 0 10px;

        &.vs-hide {
          visibility: hidden;
        }

        &.cancel {
          text-decoration: line-through;
          color: #919191;
        }

        &:first-child {
          padding-left: 0;
        }

        &:last-child {
          text-align: right;
          padding-right: 0;
        }

        &:nth-child(1) {
          width: 25%;
        }

        &:nth-child(2) {
          width: 20%;
        }

        &:nth-child(3) {
          width: 10%;
        }

        &:nth-child(4) {
          width: 10%;
        }

        &:nth-child(5) {
          width: 10%;
        }
      }

      &:after {
        content: '';
        position: absolute;
        border-bottom: 1px solid $color-border-base;
        bottom: 0;
        left: 0;
        right: 0;
        margin: 0 20px;
      }
    }

    .footer {
      text-align: right;
      padding: 20px;

      > div {
        line-height: 20px;
      }

      .total {
        color: $color-text-light;
        margin-bottom: 10px;
      }

      .unpaid {
        color: #e35d5d;
      }
    }
  }
}

.order-expanded2 {
  display: flex;
  flex-direction: column;
  margin-bottom: 5px;

  :global {
    .dividerToRight {
      width: 90%;
      position: relative;
      left: 10%;
    }

    .expandedItems {
      display: flex;

      > div {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;

        &:nth-child(1) {
          width: 10%;
        }

        &:nth-child(2) {
          width: 10%;
        }

        &:nth-child(3) {
          width: 29%;
        }

        &:nth-child(4) {
          width: 17%;
        }

        &:nth-child(5) {
          display: flex;
          flex-direction: column;

          .order-item {
            width: 100%;
            display: flex;
            justify-content: space-between;
            padding-bottom: 10px;
          }

          width: 27%;
        }
      }
    }

    .childrenExpandedItems {
      display: flex;
      margin-top: 20px;

      > div {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;

        &:nth-child(1) {
          width: 10%;
          position: relative;
          left: 6%;
        }

        &:nth-child(2) {
          width: 10%;
          position: relative;
          left: 4%;
        }

        &:nth-child(3) {
          width: 29%;
          position: relative;
          left: 2%;
        }

        &:nth-child(4) {
          width: 17%;
        }

        &:nth-child(5) {
          display: flex;
          flex-direction: column;

          .order-item {
            width: 100%;
            display: flex;
            justify-content: space-between;
            padding-bottom: 10px;
          }

          width: 27%;
        }
      }
    }

    .tRight {
      display: inline-block;
      width: 88%;
      text-align: right;
      padding-right: 10px;
    }

    .tLeft {
      display: inline-block;
      width: 12%;
      text-align: left;
    }

    .footer {
      text-align: right;
      padding: 20px 0 0 20px;

      > div {
        line-height: 20px;
      }

      .total {
        color: $color-text-light;
        margin-bottom: 10px;
      }

      .unpaid {
        color: #e35d5d;
      }
    }
  }
}

.btnTop {
  padding: 0.5rem 0;

  @include max-sm {
    display: flex;
    flex-direction: column;
  }

  :global {
    .side {
      @include max-sm {
        display: flex;
      }

      &:first-child {
        @include max-sm {
          margin-bottom: 0.5rem;

          .el-button {
            flex: 1 0 0;
            width: 50%;
          }
        }
      }

      .el-input {
        @include max-sm {
          margin-left: 0 !important;
          width: 100%;
        }
      }
    }
  }
}

.next {
  position: absolute;
  top: 0;
  width: 10px;
  height: 65px;
  border-left: 1px solid $color-text-placeholder;
  border-bottom: 1px solid $color-text-placeholder;
}
</style>
